import { useCallback, useEffect, useRef } from "react";
import * as Option from "@utils/Option";
import type { DependencyList } from "react";

import type { IOts, OptionTs, TaskEitherTs, TaskTs } from "@ps-bridge/interop";
import type { EmitOnRouteChangeTask } from "@ps-bridge/capabilitiesTypes";
import { ok, type Ok } from "@ps-bridge/ok";
import {
    defaultTaskErrorFormatter,
    runIO,
    runTaskEither,
    runTaskEitherWithError,
    type TaskEitherErrorFormatter,
    type TaskEitherHandlers,
} from "@view/effects/effectRunner";
import { useLoadingController } from "@contexts/Loading.context";
import { toLoadingErrorInput } from "@utils/loadingError";
import * as IO from "@ps-bridge/interop/io";
import { taskEitherFromTask } from "@utils/effects/branded";

export const useRunIO = () =>
    useCallback(<Value>(io: IOts<Value>): Value => runIO(io), []);

const runTaskWithError = <Value>(
    task: TaskTs<Value>,
    triggerErrorState: (message: string, info: OptionTs<string>) => IOts<Ok>,
): Promise<Ok> =>
    runTaskEitherWithError(
        taskEitherFromTask(task, defaultTaskErrorFormatter),
        triggerErrorState,
        (error) => error,
    ).then(() => ok);

const ignoreTaskError = (_message: string, _info: OptionTs<string>): IOts<Ok> => IO.pure(ok);

export const useRunTask = () =>
    useCallback(<Value>(task: TaskTs<Value>): Promise<Ok> => runTaskWithError(task, ignoreTaskError), []);

export const useRunTaskWithError = () => {
    const { setError } = useLoadingController();
    const triggerErrorState = useCallback(
        (message: string, info: OptionTs<string>) => setError(toLoadingErrorInput(message, info)),
        [setError],
    );
    return useCallback(
        <Value>(task: TaskTs<Value>): Promise<Ok> => runTaskWithError(task, triggerErrorState),
        [triggerErrorState],
    );
};

export const useRunTaskEither = () =>
    useCallback(<Error, Value>(
        task: TaskEitherTs<Error, Value>,
        handlers: TaskEitherHandlers<Error, Value>,
    ): Promise<Ok> => runTaskEither(task, handlers), []);

export const useRunTaskEitherWithError = () => {
    const { setError } = useLoadingController();
    const triggerErrorState = useCallback(
        (message: string, info: OptionTs<string>) => setError(toLoadingErrorInput(message, info)),
        [setError],
    );
    return useCallback(
        <Error, Value>(
            task: TaskEitherTs<Error, Value>,
            formatError: TaskEitherErrorFormatter<Error>,
        ): Promise<import("fp-ts/lib/Either").Either<Error, Value>> =>
            runTaskEitherWithError(task, triggerErrorState, formatError),
        [triggerErrorState],
    );
};

export const useTaskEffect = <Value>(
    task: TaskTs<Value>,
): Ok => {
    useEffect(() => {
        runTaskWithError(task, ignoreTaskError);
    }, [task]);
    return ok;
};

export const useTaskEffectOption = <Value>(
    task: OptionTs<TaskTs<Value>>,
): Ok => {
    useEffect(() => {
        Option.fold(task, () => undefined, (taskValue: TaskTs<Value>) => {
                runTaskWithError(taskValue, ignoreTaskError);
            },);
    }, [task]);
    return ok;
};

export const useTaskOnInit = <Value>(
    task: OptionTs<TaskTs<Value>>,
    deps?: DependencyList,
): Ok => {
    const resolvedDeps = deps ?? [task];
    const hasRunRef = useRef(false);
    useEffect(() => {
        if (hasRunRef.current) return;
        Option.fold(task, () => undefined, (taskValue: TaskTs<Value>) => {
            runTaskWithError(taskValue, ignoreTaskError);
            hasRunRef.current = true;
        });
        // caller-provided deps determine when init availability is checked; effect still runs at most once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, resolvedDeps);
    return ok;
};

export const useTaskOnChange = <Value>(
    task: TaskTs<Value>,
    deps: DependencyList,
): Ok => {
    useEffect(() => {
        runTaskWithError(task, ignoreTaskError);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- caller-provided deps define rerun semantics.
    }, deps);
    return ok;
};

export const useTaskOnChangeOption = <Value>(
    task: OptionTs<TaskTs<Value>>,
    deps: DependencyList,
): Ok => {
    useEffect(() => {
        Option.fold(task, () => undefined, (taskValue: TaskTs<Value>) => {
            runTaskWithError(taskValue, ignoreTaskError);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- caller-provided deps define rerun semantics.
    }, deps);
    return ok;
};

export const useOnRouteChangeTask = <Route>(
    route: OptionTs<Route>,
    emit: EmitOnRouteChangeTask<OptionTs<Route>>,
): Ok => {
    const runTaskWithError = useRunTaskWithError();
    useEffect(() => {
        runTaskWithError(emit(route));
    }, [emit, route, runTaskWithError]);
    return ok;
};
